import { useFeatureFlagPayload } from '@posthog/react'
import { useEffect } from 'react'

import { CURRENT_BUILD_ID, canReloadNow, isBuildOutdated, markReloaded } from '@/utils'

export const MIN_SUPPORTED_WEB_VERSION_FLAG = 'min_supported_web_version'

// Note: `useFeatureFlagPayload` only re-renders when the payload value itself changes, so
// without a retry an attempt blocked by `canReloadNow`'s cooldown would never be retried —
// PostHog re-delivering the same payload doesn't trigger the effect again. Keep this well
// under `RELOAD_COOLDOWN_MS` so we retry a few times over the cooldown window rather than
// racing to reload right as it clears.
const RETRY_INTERVAL_MS = 30 * 1000

/**
 * Tier 3 — forced reload, gated on the `min_supported_web_version` PostHog feature flag
 * (its payload holds the minimum build id still allowed to run). When the running build
 * is older, reload as soon as possible — no confirmation, no banner, and (unlike Tier 1)
 * ignoring the popup/dirty-form safety checks entirely, since this tier exists precisely
 * for releases where staying on the old build is actively harmful. Retries on an interval
 * until the reload goes through, so a cooldown-blocked attempt isn't stuck waiting on the
 * flag payload to change again.
 *
 * Still respects the shared reload-loop cooldown (`canReloadNow`) as a hard safety net —
 * dropping it here would turn any bad deploy/flag payload into an infinite reload loop
 * instead of just a stale client. See EL-13298.
 */
export const useForcedReload = () => {
  const minSupportedWebVersion = useFeatureFlagPayload(MIN_SUPPORTED_WEB_VERSION_FLAG)

  useEffect(() => {
    if (typeof minSupportedWebVersion !== 'string') return
    if (!isBuildOutdated(CURRENT_BUILD_ID, minSupportedWebVersion)) return

    const attemptReload = () => {
      if (!canReloadNow()) return

      markReloaded()
      window.location.reload()
    }

    attemptReload()
    const intervalId = window.setInterval(attemptReload, RETRY_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [minSupportedWebVersion])
}
